// InterestRegister — soft "register your interest" prompt that replaces the
// transactional Waitlist sign-up at the end of the unified landing. Reuses
// the existing /api/waitlist endpoint (same collection, no schema break).
// Copy is placeholder — Glenn/Lea will rewrite the narrative.
import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { FmnWatermark } from "../FmnWatermark";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const FADE = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] },
};

const RELATION_KEYS = [
    "baby_boy", "baby_girl",
    "parent", "spouse", "grandparent", "sibling",
    "friend", "self", "aged_care", "other",
];

export function InterestRegister() {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [relation, setRelation] = useState("");
    const [excitement, setExcitement] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            // Reuse /api/waitlist — payload extended with optional `notes` field.
            await api.post("/waitlist", { email, name, relation, notes: excitement || undefined });
            setDone(true);
            toast.success(t("interest_register.success"));
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("waitlist.toast_error"));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <section
            className="relative py-32 px-6 rem-petal-bg overflow-hidden"
            data-testid="interest-register-section"
            id="interest"
        >
            <FmnWatermark position="left" size={740} opacity={0.05} rotate={9} />
            <motion.div {...FADE} className="max-w-2xl mx-auto text-center">
                <div className="flex justify-center mb-6">
                    <ForgetMeNotIcon size={44} />
                </div>
                <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">
                    {t("interest_register.eyebrow")}
                </p>
                <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-tight">
                    {t("interest_register.headline_part1")}{" "}
                    <em className="italic text-[#4A5A95]">{t("interest_register.headline_emphasis")}</em>
                </h2>
                <p className="mt-7 text-[#5C5A59] leading-relaxed font-light">
                    {t("interest_register.narrative_placeholder")}
                </p>

                {done ? (
                    <div
                        className="mt-10 rounded-2xl bg-[#F4F6FB] p-8 text-[#4A5A95] font-medium"
                        data-testid="interest-register-success"
                    >
                        {t("interest_register.success")}
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-10 space-y-4 text-left" data-testid="interest-register-form">
                        <div>
                            <label className="text-xs uppercase tracking-[0.22em] text-[#5C5A59] font-semibold">
                                {t("interest_register.label_email")}
                            </label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-2 w-full rounded-2xl border border-[#E5E1D8] bg-white px-5 py-3.5 text-[#2D2B2A] focus:outline-none focus:border-[#6B7DB8] transition-colors"
                                placeholder="you@example.com"
                                data-testid="interest-register-email"
                            />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs uppercase tracking-[0.22em] text-[#5C5A59] font-semibold">
                                    {t("interest_register.label_name")}
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="mt-2 w-full rounded-2xl border border-[#E5E1D8] bg-white px-5 py-3.5 text-[#2D2B2A] focus:outline-none focus:border-[#6B7DB8] transition-colors"
                                    data-testid="interest-register-name"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.22em] text-[#5C5A59] font-semibold">
                                    {t("interest_register.label_relation")}
                                </label>
                                <select
                                    value={relation}
                                    onChange={(e) => setRelation(e.target.value)}
                                    className="mt-2 w-full rounded-2xl border border-[#E5E1D8] bg-white px-5 py-3.5 text-[#2D2B2A] focus:outline-none focus:border-[#6B7DB8] transition-colors"
                                    data-testid="interest-register-relation"
                                >
                                    <option value="">—</option>
                                    {RELATION_KEYS.map((k) => (
                                        <option key={k} value={k}>{t(`waitlist.relation.${k}`)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.22em] text-[#5C5A59] font-semibold">
                                {t("interest_register.label_excitement")}
                            </label>
                            <textarea
                                value={excitement}
                                onChange={(e) => setExcitement(e.target.value)}
                                rows={3}
                                className="mt-2 w-full rounded-2xl border border-[#E5E1D8] bg-white px-5 py-3.5 text-[#2D2B2A] focus:outline-none focus:border-[#6B7DB8] transition-colors resize-none"
                                data-testid="interest-register-excitement"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full inline-flex items-center justify-center gap-2 mt-2 px-9 py-4 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white font-medium text-base disabled:opacity-60 transition-all hover:-translate-y-0.5 shadow-rem-soft"
                            data-testid="interest-register-submit"
                        >
                            {submitting ? "…" : t("interest_register.submit")}
                            {!submitting && <ArrowRight weight="bold" size={18} />}
                        </button>
                        <p className="text-xs text-center text-[#8A8786] mt-3">
                            {t("interest_register.footnote_privacy")}
                        </p>
                    </form>
                )}
            </motion.div>
        </section>
    );
}
